//used for sending large binary files in chunks
//an "ack" message is required after every chunk to continue
export class ChunkedBufferSender {
    constructor(ws, chunkSize = 64 * 1024) {
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            throw new Error('A connected and open WebSocket instance is required.');
        }
        this.ws = ws;
        this.chunkSize = chunkSize;
        this.resolveAck = null;
        this.isSending = false;
        
        // Keep track of the original binary type and handler to restore later
        this.originalBinaryType = ws.binaryType;
        this.originalOnMessage = null;
    }

    /**
     * Internal message handler that filters for "ack" strings.
     * If a message isn't an "ack", it passes it through to the original handler 
     * so normal traffic isn't completely dropped during the transfer.
     */
    _handleMessage(event) {
        let message = event.data;
        
        if (message instanceof ArrayBuffer) {
            message = new TextDecoder().decode(message);
        }

        if (message === 'ack') {
            if (this.resolveAck) {
                const resolve = this.resolveAck;
                this.resolveAck = null;
                resolve();
            }
        } else {
            // Forward non-ack messages to the original handler just in case
            if (typeof this.originalOnMessage === 'function') {
                this.originalOnMessage.call(this.ws, event);
            }
        }
    }

    /**
     * Sends an ArrayBuffer in chunks, hijacking the socket until finished.
     * @param {ArrayBuffer} arrayBuffer - The data to send.
     */
    async send(arrayBuffer) {
        if (this.isSending) {
            throw new Error('A chunked send operation is already in progress.');
        }
        if (this.ws.readyState !== WebSocket.OPEN) {
            throw new Error('WebSocket was closed before sending could begin.');
        }

        this.isSending = true;
        
        // 1. Enter "Large Data Mode": Save existing state and hijack the handler
        this.originalOnMessage = this.ws.onmessage;
        this.ws.binaryType = 'arraybuffer'; 
        this.ws.onmessage = (event) => this._handleMessage(event);

        const totalBytes = arrayBuffer.byteLength;
        let offset = 0;

        try {
            while (offset < totalBytes) {
                const end = Math.min(offset + this.chunkSize, totalBytes);
                const chunk = arrayBuffer.slice(offset, end);

                const ackPromise = new Promise((resolve, reject) => {
                    this.resolveAck = resolve;
                    
                    // Guard against the socket closing mid-transfer
                    this.ws.addEventListener('close', () => reject(new Error('WebSocket closed mid-transfer.')), { once: true });
                    this.ws.addEventListener('error', (err) => reject(err), { once: true });
                });

                // Send the binary chunk
                this.ws.send(chunk);
                
                // Block until 'ack' is received
                await ackPromise;

                offset = end;
                console.log(`Sent ${offset}/${totalBytes} bytes (${((offset / totalBytes) * 100).toFixed(1)}%)`);
            }
            console.log('Large data transfer completed successfully.');
        } catch (error) {
            console.error('Large data transfer aborted:', error);
            throw error;
        } finally {
            // 2. Exit "Large Data Mode": Restore the socket to its original state
            this._restoreSocket();
        }
    }

    /**
     * Cleans up hooks and restores the original WebSocket configuration.
     */
    _restoreSocket() {
        if (this.ws) {
            this.ws.onmessage = this.originalOnMessage;
            this.ws.binaryType = this.originalBinaryType;
        }
        this.resolveAck = null;
        this.originalOnMessage = null;
        this.isSending = false;
    }
}